/**
 * Pre-configured format templates (PRD §6.5, PRD-P1 §7.2).
 *
 * Each template is a complete `RuleSet` except for `id`, which is a database
 * default and differs per environment. The template carries a stable `name` that
 * the setup screen shows and the server matches to an `id` on creation.
 *
 * Templates are data, not code. Adding a new format is adding an object here and
 * a YAML fixture that scores a full match under it.
 */
import type { RuleSet } from "./ruleset.ts";

/**
 * A format template is a `RuleSet` without the database-assigned `id`. The
 * server stamps one on insertion; the client never invents an id (PRD §6.5).
 */
export type FormatTemplate = Omit<RuleSet, "id">;

const T20: FormatTemplate = {
  name: "T20",
  version: 1,
  playersPerSide: 11,
  overs: 20,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 4,
  consecutiveOversAllowed: false,
  lbwEnabled: true,
  noBallFreeHit: true,
  wideRule: "standard",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: false,
  oneTipOneHandOut: false,
  oneBounceCatchOut: false,
  retiredReturnsAllowed: true,
};

const ODI_40: FormatTemplate = {
  name: "ODI-40",
  version: 1,
  playersPerSide: 11,
  overs: 40,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 8,
  consecutiveOversAllowed: false,
  lbwEnabled: true,
  noBallFreeHit: true,
  wideRule: "standard",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: false,
  oneTipOneHandOut: false,
  oneBounceCatchOut: false,
  retiredReturnsAllowed: true,
};

const ODI_50: FormatTemplate = {
  name: "ODI-50",
  version: 1,
  playersPerSide: 11,
  overs: 50,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 10,
  consecutiveOversAllowed: false,
  lbwEnabled: true,
  noBallFreeHit: true,
  wideRule: "standard",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: false,
  oneTipOneHandOut: false,
  oneBounceCatchOut: false,
  retiredReturnsAllowed: true,
};

const CORPORATE_8: FormatTemplate = {
  name: "8-a-side Corporate",
  version: 1,
  playersPerSide: 8,
  overs: 8,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 2,
  consecutiveOversAllowed: false,
  lbwEnabled: false,
  noBallFreeHit: true,
  wideRule: "leg_side_only",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: false,
  oneTipOneHandOut: false,
  oneBounceCatchOut: false,
  retiredReturnsAllowed: true,
};

const BOX_CRICKET: FormatTemplate = {
  name: "Box Cricket",
  version: 1,
  playersPerSide: 6,
  overs: 6,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 2,
  consecutiveOversAllowed: false,
  lbwEnabled: false,
  noBallFreeHit: true,
  wideRule: "leg_side_only",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: true,
  oneTipOneHandOut: true,
  oneBounceCatchOut: false,
  retiredReturnsAllowed: true,
};

const TAPE_BALL: FormatTemplate = {
  name: "Tape Ball",
  version: 1,
  playersPerSide: 11,
  overs: 15,
  ballsPerOver: 6,
  inningsPerSide: 1,
  maxWickets: null,
  maxOversPerBowler: 3,
  consecutiveOversAllowed: false,
  lbwEnabled: false,
  noBallFreeHit: true,
  wideRule: "standard",
  byesBreakMaiden: false,
  newBatterTakesStrikeOnCaught: true,
  superOverOnTie: true,
  lastManStands: false,
  oneTipOneHandOut: true,
  oneBounceCatchOut: true,
  retiredReturnsAllowed: true,
};

/**
 * Every format the setup screen offers, keyed by a stable slug. The order is the
 * order the UI renders them in.
 */
export const FORMAT_TEMPLATES: readonly FormatTemplate[] = [
  T20,
  ODI_40,
  ODI_50,
  CORPORATE_8,
  BOX_CRICKET,
  TAPE_BALL,
];
